/**
 * Dev verify mode (W1-05b engine-graph; ARCHITECTURE.md §7.1, §14): recompile the whole chart from scratch and
 * list every difference to the incrementally maintained graph. An empty list means the dirty DAG missed nothing.
 *
 * The reference is built with nothing incremental: the lowering options, the effective package settings and the
 * floor speed settings RE-DERIVED from `doc.pkg` (plus the engine's explicit overrides), `Lower.lowerChart` (the
 * exporter's own entry point) and W1-05a's `compileChartEval` (tempo, bezier registry, max_time, every line,
 * floors), plus a fresh notes-seam / effects-seam context over them (its own state pool, fresh stores, a fresh
 * path -> asset lookup). Compared, bit for bit (`Object.is`, so NaN equals NaN and -0 differs from 0):
 *   - the lowering options, effective pkg and speed settings the graph holds against the re-derived ones;
 *   - the chart IR, the extra.json IR and the lowering report (structural, with JSON-path-like locations);
 *   - the tempo map points, max_time, the bezier registry signature, the father topology and the draw order;
 *   - every line's state (every LineStateRt field), floor position, effective speed, channel readouts and note
 *     control curves at sample times spread over the chart (BPM changes, max_time and its neighbourhood);
 *   - through the notes seam: every note's seconds, end seconds and multi-highlight flag, the autoplay emissions
 *     of the whole chart, the judged-play candidate list and the HUD values at the sample times;
 *   - the frame output at the sample times, composed exactly like `Engine.frame` (R§8) in the window of the last
 *     frame drawn (else 1920x1080 with Phira's default respack metrics), ghosts and HUD on, autoplay: line bodies,
 *     note quads (positions, uvs, colours, slots, note ids, buckets), effects and videos, hit effects, particles,
 *     HUD and ghost outlines. Note heights live only in the notes seam's runtimes, so this is what catches a
 *     runtime built from an old floor curve;
 *   - the read-time query `noteAppearSec` for up to VERIFY_APPEAR_NOTES notes spread over the chart.
 * At most `limit` differences are reported (a corrupted graph usually differs everywhere).
 */

import { compileChartEval, type ChartEval } from './chartEval';
import { LineScene } from './compose';
import type { ControlWhich } from './controls';
import { createFrameList, fillFrameContent, reuseFrameList, type FrameSources } from './frame';
import {
  assetPathMap,
  deriveLowerOptions,
  drawInfoOf,
  drawOrderOf,
  effectivePkgOf,
  gifTotalMsOf,
  lookupAssetPath,
  speedSettingsOf,
  type EngineGraph,
  type LineDrawInfo,
} from './graph';
import type { LineStateBuf, SpeedSettings } from './lineState';
import type {
  Emission,
  EngineCompileContext,
  FrameDrawList,
  FrameOptions,
  LineStateRt,
  PhiraPlayerSettings,
  PreviewFilters,
  SkinMetrics,
} from './types';
import { chartAspect, computeViewport } from './viewport';
import { Lower } from '../ir/lower';
import type { IrChart, IrExtraJson, IrLine, LowerOptions, LowerReport } from '../ir/types';
import type { AssetId, LineId, LineProp, NoteId, PackageSettings } from '../model/types';
import type { Sec, TempoMap } from '../time/types';

/** Default maximum number of reported differences. */
export const VERIFY_LIMIT = 50;
/** Notes whose `noteAppearSec` is compared (spread evenly over the notes sorted by id). */
export const VERIFY_APPEAR_NOTES = 48;

const NO_FILTERS: PreviewFilters = {
  hiddenLines: new Set(),
  soloLines: new Set(),
  mutedLayers: new Map(),
  dim: null,
  focusHitsoundLines: new Set(),
};

/** Phira's default player settings (verification window when no frame was drawn yet). */
export const VERIFY_PLAYER: PhiraPlayerSettings = {
  aspectOverride: null,
  noteScale: 1,
  doubleHint: true,
  particles: true,
  aggressiveCull: true,
  strictAutoplayHitColor: true,
  msaa: false,
};

/** Metrics of Phira's default respack (S§3.6), used when no frame was drawn yet. */
export const VERIFY_SKIN: SkinMetrics = {
  tex: {
    click: [998, 100],
    hold: [1000, 1100],
    flick: [998, 172],
    drag: [948, 50],
    click_mh: [1148, 250],
    hold_mh: [1150, 1250],
    flick_mh: [1148, 322],
    drag_mh: [1098, 200],
    holdBody: [1000, 1000],
    holdBodyMh: [1150, 990],
  },
  holdAtlas: [50, 50],
  holdAtlasMH: [130, 130],
  hitFx: [8, 7],
  hitFxDuration: 0.5,
  hitFxScale: 0.9,
  hitFxRotate: false,
  hitFxTinted: true,
  hideParticles: false,
  holdKeepHead: false,
  holdRepeat: false,
  holdCompact: false,
  // 0xe1ffec9f and 0xebb4e1ff (ARGB).
  colorPerfect: [0xff / 255, 0xec / 255, 0x9f / 255, 0xe1 / 255],
  colorGood: [0xb4 / 255, 0xe1 / 255, 0xff / 255, 0xeb / 255],
};

const DEFAULT_VERIFY_FRAME: FrameOptions = {
  winW: 1920,
  winH: 1080,
  player: VERIFY_PLAYER,
  skin: VERIFY_SKIN,
  resAlpha: 1,
  hudProgress: 1,
  showHud: true,
  showEffects: true,
  showVideos: true,
  rate: 1,
  trackLengthSec: 120,
  filters: NO_FILTERS,
  ghosts: true,
  judge: null,
};

/**
 * Frame options verify mode composes frames with: the given window, player, skin and filters (the last frame
 * drawn) or the defaults, with ghosts, HUD, effects and videos on and autoplay semantics (judged play keeps state
 * across frames that a fresh reference cannot have).
 */
export function verifyFrameOptions(base?: FrameOptions): FrameOptions {
  return {
    ...(base ?? DEFAULT_VERIFY_FRAME),
    ghosts: true,
    showHud: true,
    showEffects: true,
    showVideos: true,
    judge: null,
  };
}

const LINE_PROPS: readonly LineProp[] = [
  'alpha',
  'moveX',
  'moveY',
  'rotate',
  'speed',
  'scaleX',
  'scaleY',
  'incline',
  'paint',
  'gif',
];
const CONTROLS: readonly ControlWhich[] = ['pos', 'alpha', 'size', 'y'];
const CONTROL_KEYS = [-450, 0, 120, 450, 900, 5000];

/** Collects differences up to a limit. */
export class DiffSink {
  readonly out: string[] = [];
  private readonly limit: number;

  constructor(limit: number) {
    this.limit = limit;
  }

  isFull(): boolean {
    return this.out.length >= this.limit;
  }

  add(msg: string): void {
    if (this.out.length < this.limit) this.out.push(msg);
  }
}

function show(v: unknown): string {
  if (typeof v === 'number') return Object.is(v, -0) ? '-0' : String(v);
  if (typeof v === 'string') return JSON.stringify(v);
  if (v === undefined) return 'undefined';
  try {
    const s = JSON.stringify(v);
    return s.length > 120 ? `${s.slice(0, 120)}...` : s;
  } catch {
    return Object.prototype.toString.call(v);
  }
}

/** Structural comparison of JSON-like values (arrays, plain objects, primitives); Object.is on leaves. */
export function deepDiff(a: unknown, b: unknown, path: string, sink: DiffSink): void {
  if (sink.isFull()) return;
  if (Object.is(a, b)) return;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    sink.add(`${path}: ${show(a)} (incremental) != ${show(b)} (full)`);
    return;
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) {
      sink.add(`${path}: ${show(a)} (incremental) != ${show(b)} (full)`);
      return;
    }
    if (a.length !== b.length) sink.add(`${path}.length: ${a.length} (incremental) != ${b.length} (full)`);
    const n = Math.min(a.length, b.length);
    for (let i = 0; i < n && !sink.isFull(); i++) deepDiff(a[i], b[i], `${path}[${i}]`, sink);
    return;
  }
  const ra = a as Record<string, unknown>;
  const rb = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ra), ...Object.keys(rb)]);
  for (const k of keys) {
    if (sink.isFull()) return;
    const ha = Object.prototype.hasOwnProperty.call(ra, k);
    const hb = Object.prototype.hasOwnProperty.call(rb, k);
    if (ha !== hb) {
      sink.add(`${path}.${k}: ${ha ? 'present' : 'absent'} (incremental) != ${hb ? 'present' : 'absent'} (full)`);
      continue;
    }
    deepDiff(ra[k], rb[k], `${path}.${k}`, sink);
  }
}

/** Full, non-incremental compilation of the graph's current document, with options re-derived from doc.pkg. */
export interface FullCompile {
  readonly chart: IrChart;
  readonly extra: IrExtraJson | null;
  readonly report: LowerReport;
  readonly ce: ChartEval;
  /** LineId -> export index of the full compile. */
  readonly indexOf: ReadonlyMap<LineId, number>;
  /** Lowering options re-derived from doc.pkg and the engine's explicit overrides / asset-path mapping. */
  readonly lowerOpts: LowerOptions;
  /** doc.pkg with the lowering's speed mode applied (the seams' `ctx.pkg`). */
  readonly pkg: PackageSettings;
  /** Floor speed settings re-derived from doc.pkg. */
  readonly speed: SpeedSettings;
}

export function fullCompile(g: EngineGraph): FullCompile {
  const doc = g.src.doc;
  const idx = g.src.idx;
  const lowerOpts = deriveLowerOptions(doc.pkg, g.opts.lower);
  const lowered = Lower.lowerChart(doc, idx, lowerOpts);
  const order = doc.lineOrder;
  const ce = compileChartEval(lowered.chart, {
    speedMode: lowerOpts.speedMode,
    lineId: (i, ir) => ir.srcLine ?? (order[i] as LineId),
    gifTotalMs: (i, ir) => gifTotalMsOf(doc, doc.lines.get(ir.srcLine ?? (order[i] as LineId))),
  });
  const indexOf = new Map<LineId, number>();
  order.forEach((id, i) => indexOf.set(id, i));
  return {
    chart: lowered.chart,
    extra: lowered.extra,
    report: lowered.report,
    ce,
    indexOf,
    lowerOpts,
    pkg: effectivePkgOf(doc.pkg, lowerOpts),
    speed: speedSettingsOf(doc.pkg, lowerOpts),
  };
}

/** The notes-seam / effects-seam reference context over a full compile. */
export interface ReferenceContext extends EngineCompileContext {
  /** Package path -> asset id, from a lookup built afresh over doc.assets and the re-derived lowering options. */
  assetIdForPath(path: string): AssetId | null; // (contract member, documented here for the reference lookup)
  /** Aspect ratio and preview filters `lineState` evaluates with (initially the chart aspect, no filters). */
  setView(ar: number, filters: PreviewFilters | undefined): void;
}

/**
 * An EngineCompileContext over a full compile with fresh seam stores and its own line-state pool (separate from
 * the full compile's `lineStates` pool, like the engine's, CR-W1-05b-2).
 */
export function fullContext(g: EngineGraph, f: FullCompile): ReferenceContext {
  const doc = g.src.doc;
  const order = [...doc.lineOrder];
  const indexOf = (line: LineId): number => {
    const i = f.indexOf.get(line);
    if (i === undefined) throw new RangeError(`verify: line ${String(line)} is not in the chart`);
    return i;
  };
  const pool = new LineScene(f.ce.lines);
  const assets = assetPathMap(doc.assets, f.lowerOpts.assetPath);
  let ar = chartAspect(doc.pkg);
  let filters: PreviewFilters | undefined;
  const ctx: ReferenceContext = {
    tempo: f.ce.tempo,
    maxTime: f.ce.maxTime,
    pkg: f.pkg,
    meta: doc.meta,
    irLine: (line) => f.chart.judgeLineList[indexOf(line)] as IrLine,
    irExtra: () => f.extra,
    lineOrder: () => order,
    floorAt: (line, t) => f.ce.floorAt(indexOf(line), t),
    lineState: (line, t) => pool.evalLine(indexOf(line), t, ar, filters),
    control: (line, which, h) => f.ce.control(indexOf(line), which, h),
    notesStore: new Map(),
    effectsStore: new Map(),
    assetIdForPath: (path) => lookupAssetPath(assets, path),
    setView: (a, fl) => {
      ar = a;
      filters = fl;
    },
  };
  return ctx;
}

/** Times at which line evaluation is compared. */
export function sampleTimes(tempo: TempoMap, maxTime: Sec): number[] {
  const ts = new Set<number>([-1, 0, 0.5, maxTime - 1, maxTime - 1e-3, maxTime, maxTime + 0.5]);
  for (let k = 1; k <= 7; k++) ts.add((maxTime * k) / 8);
  for (const p of tempo.points.slice(0, 8)) {
    ts.add(p.time);
    ts.add(p.time + 0.25);
  }
  return [...ts].filter((t) => Number.isFinite(t)).sort((a, b) => a - b);
}

const STATE_FIELDS: readonly (keyof LineStateRt)[] = [
  'line',
  'index',
  'kind',
  'uiMover',
  'alpha',
  'ownX',
  'ownY',
  'ownRot',
  'x',
  'y',
  'rot',
  'sx',
  'sy',
  'scaleYRaw',
  'color',
  'text',
  'height',
  'inclineSin',
  'gifProgress',
  'paint',
  'drawsNothing',
  'm',
];

function compareState(a: LineStateBuf, b: LineStateBuf, where: string, sink: DiffSink): void {
  for (const f of STATE_FIELDS) {
    if (sink.isFull()) return;
    deepDiff(a[f], b[f], `${where}.${f}`, sink);
  }
}

function emissionKey(e: Emission): string {
  const sound = e.sound === null ? 'none' : e.sound.kind === 'skin' ? e.sound.name : `asset:${String(e.sound.asset)}`;
  return `${String(e.time)}|${e.kind}|${String(e.note)}|${String(e.line)}|${sound}|${String(e.fx)}`;
}

/**
 * Recompiles the graph's document from scratch and returns every difference to the incremental state (empty =
 * consistent). Syncs the graph first. Evaluates into the graph's pooled line states (like any query).
 */
export function verifyGraph(g: EngineGraph, limit = VERIFY_LIMIT, frame?: FrameOptions): readonly string[] {
  g.sync();
  const sink = new DiffSink(limit);
  const f = fullCompile(g);
  const chart = g.chart();

  // Settings re-derived from doc.pkg (a missed pkg change shows here even when no output depends on it yet).
  deepDiff(g.lowerOpts, f.lowerOpts, 'lowerOptions', sink);
  deepDiff(g.pkg, f.pkg, 'pkg', sink);
  deepDiff(g.speed, f.speed, 'speedSettings', sink);

  // Lowered output.
  deepDiff(chart, f.chart, 'ir', sink);
  deepDiff(g.extra, f.extra, 'irExtra', sink);
  deepDiff(g.report(), f.report, 'lowerReport', sink);

  // Chart-level compiled nodes.
  deepDiff(g.tempo.points, f.ce.tempo.points, 'tempo.points', sink);
  if (!Object.is(g.maxTime, f.ce.maxTime))
    sink.add(`maxTime: ${show(g.maxTime)} (incremental) != ${show(f.ce.maxTime)} (full)`);
  if (g.registry.signature !== f.ce.registry.signature) {
    sink.add(`bezierRegistry: ${show(g.registry.signature)} (incremental) != ${show(f.ce.registry.signature)} (full)`);
  }
  deepDiff([...g.order], [...g.src.doc.lineOrder], 'lineOrder', sink);
  const topo = g.main.order();
  deepDiff(
    { order: Array.from(topo.order), parent: Array.from(topo.parent), broken: topo.broken },
    { order: Array.from(f.ce.order.order), parent: Array.from(f.ce.order.parent), broken: f.ce.order.broken },
    'fatherOrder',
    sink,
  );
  deepDiff(Array.from(g.drawOrder()), Array.from(drawOrderOf(f.chart.judgeLineList)), 'drawOrder', sink);
  if (sink.isFull()) return sink.out;

  // Line evaluation at sample times.
  const ar = g.chartAr();
  const times = sampleTimes(f.ce.tempo, f.ce.maxTime);
  const order = g.order;
  order.forEach((id, i) => {
    if (sink.isFull()) return;
    const le = g.lineEvalOrNull(id);
    const ref = f.ce.lines[i];
    if (le === null || ref === undefined) {
      sink.add(
        `line ${String(id)}: compiled ${le === null ? 'no' : 'a'} LineEval (incremental), ${ref === undefined ? 'none' : 'one'} (full)`,
      );
      return;
    }
    if (le.index !== ref.index) sink.add(`line ${String(id)}.index: ${le.index} (incremental) != ${ref.index} (full)`);
    if (le.floor.kind !== ref.floor.kind)
      sink.add(`line ${String(id)}.floor.kind: ${le.floor.kind} != ${ref.floor.kind}`);
    for (const t of times) {
      if (sink.isFull()) return;
      const where = `line ${String(id)} @${String(t)}`;
      const h = le.floor.heightAt(t);
      const hr = ref.floor.heightAt(t);
      if (!Object.is(h, hr)) sink.add(`${where}.floor: ${show(h)} (incremental) != ${show(hr)} (full)`);
      const v = le.floor.speedAt(t);
      const vr = ref.floor.speedAt(t);
      if (!Object.is(v, vr)) sink.add(`${where}.speed: ${show(v)} (incremental) != ${show(vr)} (full)`);
      for (const prop of LINE_PROPS) {
        const a = le.displayValue(prop, t, 'sum');
        const b = ref.displayValue(prop, t, 'sum');
        if (!Object.is(a, b)) sink.add(`${where}.channel(${prop}): ${show(a)} (incremental) != ${show(b)} (full)`);
      }
    }
    for (const which of CONTROLS) {
      for (const key of CONTROL_KEYS) {
        const a = le.controls.at(which, key);
        const b = ref.controls.at(which, key);
        if (!Object.is(a, b)) sink.add(`line ${String(id)}.control(${which}, ${key}): ${show(a)} != ${show(b)}`);
      }
    }
  });
  for (const t of times) {
    if (sink.isFull()) break;
    const mine = g.main.evalAll(t, ar);
    const ref = f.ce.lineStates(t, ar);
    if (mine.length !== ref.length) {
      sink.add(`lineStates @${String(t)}.length: ${mine.length} (incremental) != ${ref.length} (full)`);
      continue;
    }
    for (let i = 0; i < mine.length && !sink.isFull(); i++) {
      const a = mine[i];
      const b = ref[i];
      if (a !== undefined && b !== undefined) compareState(a, b, `lineState[${i}] @${String(t)}`, sink);
    }
  }
  if (sink.isFull()) return sink.out;

  // Notes seam and frame output: incremental runtimes against a fresh context over the full compile.
  const ref = fullContext(g, f);
  const ids: NoteId[] = [];
  for (const id of g.src.doc.notes.keys()) ids.push(id);
  ids.sort((a, b) => a - b);
  verifyNotes(g, ref, ids, times, sink);
  if (sink.isFull()) return sink.out;
  verifyFrames(g, f, ref, ids, times, verifyFrameOptions(frame), sink);
  return sink.out;
}

function verifyNotes(
  g: EngineGraph,
  ref: ReferenceContext,
  ids: readonly NoteId[],
  times: readonly number[],
  sink: DiffSink,
): void {
  const notes = g.seams.notes;
  const ctx = g.ctx;
  for (const id of ids) {
    if (sink.isFull()) return;
    const pairs: readonly [string, unknown, unknown][] = [
      ['sec', notes.noteSec(ctx, id), notes.noteSec(ref, id)],
      ['endSec', notes.noteEndSec(ctx, id), notes.noteEndSec(ref, id)],
      ['multiHighlight', notes.isMultiHighlight(ctx, id), notes.isMultiHighlight(ref, id)],
    ];
    for (const [what, a, b] of pairs) {
      if (!Object.is(a, b)) sink.add(`note ${String(id)}.${what}: ${show(a)} (incremental) != ${show(b)} (full)`);
    }
  }
  const lo = Number.NEGATIVE_INFINITY;
  const hi = Number.POSITIVE_INFINITY;
  const mine: Emission[] = [];
  const theirs: Emission[] = [];
  notes.emissions(ctx, lo, hi, 1, undefined, mine);
  notes.emissions(ref, lo, hi, 1, undefined, theirs);
  if (mine.length !== theirs.length)
    sink.add(`emissions.length: ${mine.length} (incremental) != ${theirs.length} (full)`);
  for (let i = 0; i < Math.min(mine.length, theirs.length) && !sink.isFull(); i++) {
    const a = mine[i];
    const b = theirs[i];
    if (a === undefined || b === undefined) continue;
    const ka = emissionKey(a);
    const kb = emissionKey(b);
    if (ka !== kb) sink.add(`emissions[${i}]: ${ka} (incremental) != ${kb} (full)`);
  }
  deepDiff([...notes.notesInWindow(ctx, lo, hi)], [...notes.notesInWindow(ref, lo, hi)], 'notesInWindow', sink);
  for (const t of times) {
    if (sink.isFull()) return;
    deepDiff(notes.hudValues(ctx, t), notes.hudValues(ref, t), `hudValues @${String(t)}`, sink);
  }
}

/** Plain, comparable copy of the drawn content of a frame list (typed buffers cut to their used length). */
export function frameSnapshot(list: FrameDrawList): Record<string, unknown> {
  const n = list.notes;
  const c = n.count;
  return {
    videos: list.videos,
    lines: list.lines,
    notes: {
      count: c,
      pos: Array.from(n.pos.subarray(0, c * 8)),
      uv: Array.from(n.uv.subarray(0, c * 4)),
      color: Array.from(n.color.subarray(0, c * 4)),
      slot: Array.from(n.slot.subarray(0, c)),
      note: Array.from(n.note.subarray(0, c)),
      buckets: n.buckets,
    },
    effects: list.effects,
    hitFx: list.hitFx,
    particles: list.particles,
    hud: list.hud,
    globalEffects: list.globalEffects,
    ghosts: list.ghosts,
  };
}

/** At most `n` items of `items`, spread evenly (all of them when there are no more than `n`). */
export function spread<T>(items: readonly T[], n: number): T[] {
  if (items.length <= n) return [...items];
  const out: T[] = [];
  for (let k = 0; k < n; k++) {
    const item = items[Math.floor((k * items.length) / n)];
    if (item !== undefined) out.push(item);
  }
  return out;
}

/**
 * Frames at the sample times composed from the graph (exactly like `Engine.frame`) and from the reference, and
 * `noteAppearSec` of a spread of notes in the same window.
 */
function verifyFrames(
  g: EngineGraph,
  f: FullCompile,
  ref: ReferenceContext,
  ids: readonly NoteId[],
  times: readonly number[],
  opts: FrameOptions,
  sink: DiffSink,
): void {
  const doc = g.src.doc;
  const { notes, effects } = g.seams;
  const vp = computeViewport(opts.winW, opts.winH, doc.pkg, opts.player.aspectOverride);
  const order = doc.lineOrder;
  const refOrder = drawOrderOf(f.chart.judgeLineList);
  const refInfo = (i: number): LineDrawInfo => {
    const id = order[i];
    return drawInfoOf(doc, id === undefined ? undefined : doc.lines.get(id));
  };
  const mine = createFrameList();
  const theirs = createFrameList();
  ref.setView(vp.ar, opts.filters);
  for (const t of times) {
    if (sink.isFull()) return;
    reuseFrameList(mine);
    reuseFrameList(theirs);
    const mineSrc: FrameSources = {
      notes,
      effects,
      ctx: g.ctx,
      states: g.main.evalAll(t, vp.ar, opts.filters),
      drawOrder: g.drawOrder(),
      drawInfo: (i) => g.drawInfoAt(i),
      pkg: doc.pkg,
    };
    g.withView(vp.ar, opts.filters, () => {
      fillFrameContent(mine, mineSrc, t, opts, vp);
    });
    const theirSrc: FrameSources = {
      notes,
      effects,
      ctx: ref,
      states: f.ce.lineStates(t, vp.ar, opts.filters),
      drawOrder: refOrder,
      drawInfo: refInfo,
      pkg: doc.pkg,
    };
    fillFrameContent(theirs, theirSrc, t, opts, vp);
    deepDiff(frameSnapshot(mine), frameSnapshot(theirs), `frame @${String(t)}`, sink);
  }
  // Read-time query in the same window (no preview filters, like Engine.noteAppearSec).
  ref.setView(vp.ar, undefined);
  const { player, skin, winW, winH } = opts;
  for (const id of spread(ids, VERIFY_APPEAR_NOTES)) {
    if (sink.isFull()) return;
    const a = g.withView(vp.ar, undefined, () => notes.noteAppearSec(g.ctx, id, player, skin, winW, winH));
    const b = notes.noteAppearSec(ref, id, player, skin, winW, winH);
    if (!Object.is(a, b)) sink.add(`note ${String(id)}.appearSec: ${show(a)} (incremental) != ${show(b)} (full)`);
  }
}
